// GtWelcomeChooser — what a signed-in account with no role sees.
//
// Replaces GtPendingApproval, which told people an email was coming that nobody
// sent, and gave them no way forward. Role comes from intent: you say what you
// came for, and the server decides what that means. Nobody waits for approval.
//
// The token cache is the trap here. Clerk keeps publicMetadata in the session
// token, so stamping a role server-side does not change what RoleGate sees.
// user.reload() refetches the user; if that still has not landed, a hard refresh
// guarantees it rather than leaving someone stuck on this screen twice.
import { useState } from 'react';
import { useAuth, useUser, useClerk } from '@clerk/clerk-react';
import API_BASE from '../../config';
import logo from '../../brand/main-logo-black.png'; // C1-LOGO

export default function GtWelcomeChooser() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const { signOut } = useClerk();
  const [step, setStep] = useState('choose');
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const [consent, setConsent] = useState(false);
  const [company, setCompany] = useState('');
  const [note, setNote] = useState('');

  const email = (user && user.primaryEmailAddress && user.primaryEmailAddress.emailAddress) || '';
  const firstName = (user && user.firstName) || '';

  const claim = async (intent, extra) => {
    setBusy(intent); setError(null);
    try {
      const token = await getToken();
      const r = await fetch(API_BASE + '/api/welcome/claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ intent, ...(extra || {}) }),
      });
      await r.json().catch(() => ({}));
      if (!r.ok) throw new Error('We could not set your account up. Try again shortly.');

      if (intent === 'company') { setStep('company_done'); return; }

      // Refetch the user so RoleGate sees the new role without a sign-out.
      try { await user.reload(); } catch (e) { /* fall through to hard refresh */ }
      const role = user && user.publicMetadata && user.publicMetadata.role;
      if (!role) window.location.reload();
    } catch (e) {
      setError(e.message);
    } finally { setBusy(null); }
  };

  return (
    <div className="gt-signin">
      <div className="gt-signin-box gtwc-box">
        <img src={logo} alt="Generative Talent Co." className="gt-signin-wordmark gtwc-logo" />

        {step === 'choose' && (
          <>
            <h1 className="gtwc-h1">{firstName ? 'Welcome, ' + firstName : 'Welcome'}</h1>
            <p className="gtwc-lede">
              You are signed in{email ? ' as ' + email : ''}. What brings you here?
            </p>

            <div className="gtwc-choices">
              <div className="gtwc-card gtwc-card--cand">
                <span className="gtwc-card-h">I am looking for a role</span>
                <span className="gtwc-card-p">
                  Browse what we are recruiting for, apply, and track where things stand.
                </span>
                <label className="gtwc-consent">
                  <input type="checkbox" checked={consent}
                    onChange={(e) => setConsent(e.target.checked)} />
                  <span>
                    Generative Talent Co (GTCO Ltd) may hold my details and use them, including
                    with artificial intelligence, to match me to roles. A person always makes the
                    final decision, and I can withdraw this at any time.
                  </span>
                </label>
                <button type="button" className="gtc-primary gtwc-go"
                  disabled={!consent || busy !== null}
                  onClick={() => claim('candidate', { consent: true })}>
                  {busy === 'candidate' ? 'Setting up your account' : 'Continue'}
                </button>
              </div>

              <button type="button" className="gtwc-card gtwc-card--co" disabled={busy !== null}
                onClick={() => setStep('company')}>
                <span className="gtwc-card-h">I am hiring</span>
                <span className="gtwc-card-p">
                  Tell us what you are building and we will get in touch.
                </span>
                <span className="gtwc-card-go">Continue</span>
              </button>
            </div>

            {error && <p className="gtwc-error">{error}</p>}
            <button type="button" className="gtwc-signout" onClick={() => signOut()}>Sign out</button>
          </>
        )}

        {step === 'company' && (
          <>
            <h1 className="gtwc-h1">Tell us about your hiring</h1>
            <p className="gtwc-lede">
              Employer accounts are not open yet. Leave your details and we will come to you
              directly in the meantime.
            </p>

            <label className="gtwc-field">
              <span>Company</span>
              <input type="text" value={company} onChange={(e) => setCompany(e.target.value)}
                placeholder="Who you work for" />
            </label>
            <label className="gtwc-field">
              <span>What are you hiring for</span>
              <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)}
                placeholder="Roles, timing, anything useful" />
            </label>

            {error && <p className="gtwc-error">{error}</p>}

            <div className="gtwc-actions">
              <button type="button" className="gtwc-back" onClick={() => { setStep('choose'); setError(null); }}>
                Back
              </button>
              <button type="button" className="gtc-primary" disabled={busy !== null}
                onClick={() => claim('company', { company_name: company, note })}>
                {busy === 'company' ? 'Sending' : 'Send'}
              </button>
            </div>
          </>
        )}

        {step === 'company_done' && (
          <>
            <h1 className="gtwc-h1">Thank you</h1>
            <p className="gtwc-lede">
              We have your details and a person will be in touch. Employer accounts open later
              this year, and we will let you know when yours is ready.
            </p>
            <p className="gtwc-lede">
              If you are also open to roles yourself, you can set this account up as a candidate
              instead.
            </p>
            <div className="gtwc-actions">
              <button type="button" className="gtwc-back" onClick={() => signOut()}>Sign out</button>
              <button type="button" className="gtc-primary" disabled={busy !== null}
                onClick={() => claim('candidate', { consent: true })}>
                {busy === 'candidate' ? 'Setting up' : 'Use as a candidate account'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
